import useSWR from 'swr';
import {
  ALGOLIA_APP_ID,
  ALGOLIA_INDEX,
  ALGOLIA_INDEX_COUNTRY_UNIQ,
  ALGOLIA_INDEX_DATA_RATE_ASC,
  ALGOLIA_SEARCH_KEY,
} from './searchUtils';
import {
  AlgoliaMetaResponse,
  AlgoliaNetworksResponse,
  AlgoliaCountriesResponse,
  AlgoliaDataRatesResponse,
} from './searchTypes';

/**
 * Given a filter string, this hook fetches the metadata for the coverage search
 * consisting of the number of networks and countries and a complete list of
 * networks with minimal CSP and pricing information.
 * @param filters - The filter string.
 * @returns The metadata for the coverage search.
 */
const fetcher = async (filters: string): Promise<AlgoliaMetaResponse> => {
  if (!filters) return { networks: 0, countries: 0, dataRates: [] };
  const baseUrl = `https://${ALGOLIA_APP_ID}.algolia.net/1/indexes`;
  const headers = {
    'X-Algolia-Application-Id': ALGOLIA_APP_ID,
    'X-Algolia-API-Key': ALGOLIA_SEARCH_KEY,
    'Content-Type': 'application/json',
  };
  const method = 'POST';
  const [networksResponse, countriesResponse, dataRatesAscResponse] = await Promise.all([
    // one API call to get total number of networks matching this filter
    fetch(`${baseUrl}/${ALGOLIA_INDEX}/query`, {
      method,
      headers,
      body: JSON.stringify({ filters, hitsPerPage: 1 }),
    }),
    // second API call to get distinct number of countries
    fetch(`${baseUrl}/${ALGOLIA_INDEX_COUNTRY_UNIQ}/query`, {
      method,
      headers,
      body: JSON.stringify({ filters, hitsPerPage: 0 }),
    }),
    // third API call to get all data rates in ascending order
    fetch(`${baseUrl}/${ALGOLIA_INDEX_DATA_RATE_ASC}/query`, {
      method,
      headers,
      body: JSON.stringify({
        filters,
        hitsPerPage: 1000,
        distinct: 1,
        attributesToRetrieve: ['data_rate'],
        attributesToHighlight: [],
      }),
    }),
  ]);

  if (!networksResponse.ok || !countriesResponse.ok) {
    throw new Error(`Error fetching search metadata`);
  }

  const networksData: AlgoliaNetworksResponse = await networksResponse.json();
  const countriesData: AlgoliaCountriesResponse = await countriesResponse.json();
  const dataRatesAscData: AlgoliaDataRatesResponse = await dataRatesAscResponse.json();
  return {
    networks: networksData.nbHits,
    countries: countriesData.nbHits,
    dataRates: dataRatesAscData.hits,
  };
};

const useAlgoliaMeta = (filters: string) => {
  const { data, error, isLoading } = useSWR<AlgoliaMetaResponse>(filters, fetcher);

  return {
    isLoading,
    error,
    data,
  };
};

export default useAlgoliaMeta;
